import config from '../config';
import { City } from '../types/City/City';

/**
 * Builds the site name, qualified with a city when one is known.
 * Format: "Everyday Rides Seattle" / "Everyday Rides"
 *
 * @param cityName - Optional city name
 * @returns The site name, city-qualified when a city is given
 */
export const buildSiteName = (cityName?: string): string => {
  const { siteName } = config().universal;
  return cityName ? `${siteName} ${cityName}` : siteName;
};

/**
 * Builds an absolute Next.js metadata title.
 * Format: "{title} | {siteName}"
 *
 * Returns `{ absolute }` deliberately: it opts the page out of the root
 * layout's '%s | Everyday Rides' template, which is what prevents the brand
 * being appended a second time.
 *
 * @param title - The page title, without any brand suffix
 * @param cityName - Optional city name
 * @returns A Next.js absolute title object
 */
export const buildMetaTitle = (title: string, cityName?: string) => ({
  absolute: `${title} | ${buildSiteName(cityName)}`,
});

/**
 * Builds a page title for client-rendered <title> tags.
 * Format: "{title} - {siteName}"
 *
 * @param title - Optional page title
 * @param city - Optional city object
 * @returns Formatted page title string
 */
export const buildTitle = (title?: string, city?: City): string => {
  return `${title ? `${title} - ` : ''}${buildSiteName(city?.name)}`;
};
